import { CommonModule } from '@angular/common';
import { Component, inject, Injector, input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { DisplayType, FieldTypeEnum } from '@models/module/fields/enums';
import {
  FontStyleType,
  HorizontalAlignType,
  LabelValueAlignmentType,
  ScreenFieldFormattingModel,
  VerticalAlignType,
} from '@models/module/fields/main';
import {
  DataCardRelationValue,
  DataCardSettingFontColorIndicatorDown,
  DataCardSettingFontColorIndicatorUp,
  DataCardTextSizeToPercent,
  DataCardViewPart,
} from '@models/module/main';
import { TableFieldModel } from '@module-definition/table-field/common/table-field.models';
import { FieldDefinitionService } from '@module-instance/services';
import { CipoStateComponent } from '@shared/components/cipo-state/state.component';
import { NumberProgressBarComponent } from '@shared/components/number-progress-bar/number-progress-bar.component';
import { PipesModule } from '@shared/modules/pipes.module';

@Component({
  selector: 'app-data-card-view-part',
  templateUrl: './data-card-view-part.component.html',
  styleUrls: ['./data-card-view-part.component.scss'],
  imports: [
    NumberProgressBarComponent,
    CipoStateComponent,
    CommonModule,
    PipesModule,
    MatIconModule,
    NgxSkeletonLoaderModule,
  ],
})
export class DataCardViewPartComponent implements OnChanges {
  private readonly fieldDefinitionService = inject(FieldDefinitionService);
  private readonly injector = inject(Injector);

  readonly part = input.required<DataCardViewPart>();
  readonly tableColumns = signal<TableFieldModel[]>([]);
  readonly tableColumnsLoaded = signal(false);

  readonly fieldTypeEnum = FieldTypeEnum;

  /** Preview mode has no real field id to fetch columns for, so show example columns instead. */
  private static readonly PREVIEW_TABLE_COLUMNS = [
    { id: 1, label: 'Column 1', name: 'column1', position: 1, formattings: [], restrictions: [] },
    { id: 2, label: 'Column 2', name: 'column2', position: 2, formattings: [], restrictions: [] },
    { id: 3, label: 'Column 3', name: 'column3', position: 3, formattings: [], restrictions: [] },
  ] as unknown as TableFieldModel[];

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.part || (!this.part()?.isTable && !this.part()?.isWeather)) {
      return;
    }
    this.tableColumnsLoaded.set(false);
    this.tableColumns.set([]);
    if (this.part().preview) {
      this.tableColumns.set(DataCardViewPartComponent.PREVIEW_TABLE_COLUMNS);
      this.tableColumnsLoaded.set(true);
      return;
    }
    if (this.part().field?.id) {
      this.fieldDefinitionService.getTableFieldColumns(this.part().field.id).subscribe(columns => {
        this.tableColumns.set((columns || []).slice().sort((a, b) => a.position - b.position));
        this.tableColumnsLoaded.set(true);
      });
    }
  }

  openRelation(event: Event, relation: DataCardRelationValue): void {
    event.stopPropagation();
    if (!relation.entityInstanceId || !relation.moduleId) {
      return;
    }
    /* Lazy-loaded: a static import here would form a circular dependency
       (this -> DocumentService -> document details -> dynamic module table -> data cards view -> this). */
    import('@shared/services/document.service').then(({ DocumentService }) => {
      this.injector.get(DocumentService).openDocument({
        entityInstanceId: relation.entityInstanceId,
        moduleId: relation.moduleId,
        contractId: relation.contractId,
      });
    });
  }

  /** No file id is available (just a name), so pick an icon from the extension instead of a real thumbnail. */
  getAttachmentIcon(name: string): string {
    const extension = name.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return 'image';
    }
    if (extension === 'pdf') {
      return 'picture_as_pdf';
    }
    if (['doc', 'docx'].includes(extension)) {
      return 'description';
    }
    if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return 'table_chart';
    }
    if (['zip', 'rar', '7z'].includes(extension)) {
      return 'folder_zip';
    }
    return 'insert_drive_file';
  }

  get showLabel(): boolean {
    return (
      [DisplayType.Text, DisplayType.CheckBox, DisplayType.Select].includes(
        this.part().field.displayTypeId || DisplayType.Text,
      ) && this.part().field.valueFormatting.size < 100
    );
  }

  get valueSize(): number {
    return this.showLabel ? this.part().field.valueFormatting.size : 100;
  }

  get isLabelValueSplit(): boolean {
    return (
      !this.part().field.valueFormatting.labelValueAlignment ||
      this.part().field.valueFormatting.labelValueAlignment == LabelValueAlignmentType.Split
    );
  }

  get isLabelValueJoin(): boolean {
    return this.part().field.valueFormatting.labelValueAlignment == LabelValueAlignmentType.Join;
  }

  get isLabelValueVertical(): boolean {
    return this.part().field.valueFormatting.labelValueAlignment == LabelValueAlignmentType.Vertical;
  }

  getFontWeight(partSetting: ScreenFieldFormattingModel): string {
    return this.hasFlag(partSetting.fontStyle, FontStyleType.Bold) ? 'bold' : 'normal';
  }

  getFontStyle(partSetting: ScreenFieldFormattingModel): string {
    return this.hasFlag(partSetting.fontStyle, FontStyleType.Italic) ? 'italic' : 'normal';
  }

  getNumberFontColor(partSetting: ScreenFieldFormattingModel): string {
    if (
      this.part().displayTypeId == DisplayType.IndicatorUp ||
      (this.part().displayTypeId == DisplayType.IndicatorBasedOnValue && this.part().valueNumber > 0) ||
      this.part().displayTypeId == DisplayType.PercentAmountProgress ||
      this.part().displayTypeId == DisplayType.PercentDurationProgress
    ) {
      return DataCardSettingFontColorIndicatorUp;
    }

    if (
      this.part().displayTypeId == DisplayType.IndicatorDown ||
      (this.part().displayTypeId == DisplayType.IndicatorBasedOnValue && this.part().valueNumber < 0)
    ) {
      return DataCardSettingFontColorIndicatorDown;
    }

    return partSetting.fontColor;
  }

  getFontColor(partSetting: ScreenFieldFormattingModel): string {
    if (this.part().isNumber) {
      return this.getNumberFontColor(partSetting);
    }
    return partSetting.fontColor;
  }

  getTextDecoration(partSetting: ScreenFieldFormattingModel): string {
    return this.hasFlag(partSetting.fontStyle, FontStyleType.Underlined) ? 'underline' : 'none';
  }

  /**
   * Main axis of the part's flex container: horizontal for Split/Join (row),
   * vertical for the Vertical alignment (column).
   */
  getJustifyContent(partSetting: ScreenFieldFormattingModel): string {
    return this.isLabelValueVertical ? this.getVerticalAlign(partSetting) : this.getHorizontalAlign(partSetting);
  }

  /** Cross axis — the opposite of {@link getJustifyContent}. Also used for `align-content`. */
  getAlignItems(partSetting: ScreenFieldFormattingModel): string {
    return this.isLabelValueVertical ? this.getHorizontalAlign(partSetting) : this.getVerticalAlign(partSetting);
  }

  private getHorizontalAlign(partSetting: ScreenFieldFormattingModel): string {
    return partSetting.alignHorizontal == HorizontalAlignType.Left
      ? 'flex-start'
      : partSetting.alignHorizontal == HorizontalAlignType.Right
        ? 'flex-end'
        : 'center';
  }

  private getVerticalAlign(partSetting: ScreenFieldFormattingModel): string {
    return partSetting.alignVertical == VerticalAlignType.Top
      ? 'flex-start'
      : partSetting.alignVertical == VerticalAlignType.Bottom
        ? 'flex-end'
        : 'center';
  }

  getFontSize(partSetting: ScreenFieldFormattingModel): string {
    return `${DataCardTextSizeToPercent.get(partSetting.fontSize)}%`;
  }

  get numberProgressValue(): number {
    return this.part().valueNumber > 100 ? 100 : this.part().valueNumber < 0 ? 0 : this.part().valueNumber;
  }

  private hasFlag(flags: number, flag: number): boolean {
    return (flags & flag) == flag;
  }
}
